import {
  LIQUID_RCE_VALUEOF_PATTERN,
  containsLiquidRcePattern,
} from "@hightouch/lib/templating/security";
import { transformV9ToV10 } from "@hightouch/lib/templating/v9-to-v10-transformer";

import { captureMessage } from "src/lib/monitoring";
import { v10Engine } from "src/utils/liquid-v10-engine";

// User-facing message intentionally does not name the offending pattern —
// we don't want to teach a curious user what to try next. Forensic detail
// (the snippet, field, full pattern) lives in the Sentry/Datadog event.
const SECURITY_ERROR_MESSAGE =
  "This template was rejected. Please contact support for more information.";

/**
 * Validates that all template-type mappings in a sync config can be parsed
 * by the v10 Liquid engine, after running the v9→v10 compatibility transformer.
 *
 * The transformer is applied first so save-time validation matches what the
 * runtime engine will actually accept: any template the runtime can rescue
 * via auto-migration is also accepted on save. This keeps customers from
 * being blocked by syntax (e.g. `{% assign x = | filter %}`) that v9 accepted
 * and the runtime rewrites transparently.
 *
 * Before parsing, every template is run through a security check that rejects
 * the `| valueOf` filter pattern — the seed of the LiquidJS v9 prototype-
 * pollution → RCE chain (Dependabot alert #1819). A production scan against
 * every Liquid-template-bearing table (1.1B+ historical sync_requests
 * included) returned zero hits, so this break-glass check breaks no existing
 * template. Detections fire a Sentry event tagged `liquid_rce_attempt` so
 * security/oncall sees the attempt in Datadog regardless of save outcome.
 *
 * Returns a map of field names to error messages for any templates that
 * fail security check or fail to parse after transformation.
 */
export function validateSyncConfigTemplates(
  config: Record<string, unknown>,
): Record<string, string> {
  const errors: Record<string, string> = {};

  const walk = (obj: unknown, path: string) => {
    if (!obj || typeof obj !== "object") return;
    if (Array.isArray(obj)) {
      obj.forEach((item, i) => walk(item, `${path}[${i}]`));
      return;
    }
    const record = obj as Record<string, unknown>;
    if (
      record.type === "template" &&
      typeof record.template === "string" &&
      record.template.includes("{")
    ) {
      const fieldName = typeof record.to === "string" ? record.to : path;

      if (containsLiquidRcePattern(record.template)) {
        reportLiquidSecurityViolation(record.template, fieldName);
        errors[fieldName] = SECURITY_ERROR_MESSAGE;
        return;
      }

      try {
        v10Engine.parse(transformV9ToV10(record.template));
      } catch (e) {
        errors[fieldName] = `v10 parse error: ${(e as Error).message}`;
      }
    }
    for (const [key, value] of Object.entries(record)) {
      walk(value, `${path}.${key}`);
    }
  };

  walk(config, "config");
  return errors;
}

/**
 * Fire-and-forget security telemetry for a Liquid RCE seed-pattern detection.
 *
 * Goes through `src/lib/monitoring.captureMessage`, which fans out to both
 * Sentry AND Datadog RUM. Tagged `security_event=liquid_rce_attempt` so
 * Datadog alerting / Slack / PagerDuty pipelines can route on it.
 * Captured at `fatal` severity because this pattern has no legitimate use
 * in customer templates (proven by the zero-hit production scan).
 */
function reportLiquidSecurityViolation(template: string, fieldName: string) {
  const match = template.match(LIQUID_RCE_VALUEOF_PATTERN);
  const matchIndex = match?.index ?? 0;
  const snippetStart = Math.max(0, matchIndex - 40);
  const snippetEnd = Math.min(
    template.length,
    matchIndex + (match?.[0]?.length ?? 0) + 40,
  );

  captureMessage(
    "[SECURITY] Liquid template `| valueOf` RCE seed pattern detected at save",
    {
      level: "fatal",
      tags: {
        security_event: "liquid_rce_attempt",
        liquid_pattern: "valueOf",
      },
      extra: {
        fieldName,
        snippet: template.slice(snippetStart, snippetEnd),
        templateLength: template.length,
      },
    },
  );
}
